import { NextSeo } from 'next-seo';
import { useTranslation } from 'react-i18next';

import { HELP_CENTER_URL } from '@highlight-cp/constants';
import { Button, Stack, Text, Title } from '@highlight-cp/core-ui';
import { ExternalLink } from '@highlight-cp/core-ui-compositions';
import AuthUIWrapper from 'app/components/AuthUIWrapper';

interface ServerErrorProps {
  // Reloads the page by default. The ErrorBoundary passes its own, to render the app again first.
  onRetry?: () => void;
}

// Error pages and the ErrorBoundary fallback have no auth state, so there is one version for
// everyone. Go to Highlight links to `/`, which sends signed-in visitors home and others to sign in.
// It's a plain anchor, not next/link: a full page load drops the state that crashed, and a
// client-side navigation would leave the ErrorBoundary showing this fallback.
const ServerError = ({ onRetry = () => window.location.reload() }: ServerErrorProps) => {
  const { t } = useTranslation();

  return (
    <>
      <NextSeo title={t('Something went wrong')} noindex />
      <AuthUIWrapper showTerms={false}>
        <Stack align="stretch" gap="md">
          <Title order={2} ta="center">
            {t('Something went wrong')}
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            {t('This is a problem on our end, not yours. Please try again in a moment.')}
          </Text>
          <Button type="button" onClick={onRetry} fullWidth>
            {t('Try again')}
          </Button>
          <Button component="a" href="/" variant="default" fullWidth>
            {t('Go to Highlight')}
          </Button>
          <Text size="sm" c="dimmed" ta="center">
            {t('Still stuck?')}{' '}
            <ExternalLink href={HELP_CENTER_URL}>{t('Visit our help center')}</ExternalLink>
          </Text>
        </Stack>
      </AuthUIWrapper>
    </>
  );
};

export default ServerError;
